import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import CommonForm from "../../../components/form/form";
import useCommonFetch from "../../../../hooks/useCommonFetch";
import DefaultButton from "../../../components/buttons/defaultButton";
import toast from "react-hot-toast";
import RoleFormSkeleton from "../../../components/skeleton/roleFormSkeleton";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import {
  createRole,
  getRoleDetail,
  updateRole,
  getRolePermissions,
} from "../../../../services/workspace/role.service";

// ── Permission pill action-type colours ──────────────────────────────────────
const ACTION_STYLES = {
  view:   { on: "bg-blue-100 text-blue-700 border-blue-200",   off: "bg-gray-50 text-gray-400 border-gray-200 hover:bg-blue-50 hover:text-blue-500 hover:border-blue-200" },
  create: { on: "bg-green-100 text-green-700 border-green-200", off: "bg-gray-50 text-gray-400 border-gray-200 hover:bg-green-50 hover:text-green-600 hover:border-green-200" },
  update: { on: "bg-amber-100 text-amber-700 border-amber-200", off: "bg-gray-50 text-gray-400 border-gray-200 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200" },
  delete: { on: "bg-red-100 text-red-700 border-red-200",       off: "bg-gray-50 text-gray-400 border-gray-200 hover:bg-red-50 hover:text-red-500 hover:border-red-200" },
};

const getActionStyle = (permName, isOn) => {
  const lower = (permName || "").toLowerCase();
  const key = Object.keys(ACTION_STYLES).find((k) => lower.endsWith(k)) || "view";
  return ACTION_STYLES[key][isOn ? "on" : "off"];
};

const formatLabel = (str) =>
  (str || "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());

// ── Indeterminate checkbox ───────────────────────────────────────────────────
const TriCheckbox = ({ id, checked, indeterminate, onChange }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      id={id}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer accent-blue-600"
    />
  );
};

const AddRole = () => {
  const navigate = useNavigate();
  const [isEditValue, setIsEditValue] = useState(null);
  const [permissionsData, setPermissionsData] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [loadingRoleData, setLoadingRoleData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);
  const { id } = useParams();

  const items = [
    { label: "Role", path: "/configuration/role" },
    { label: id ? "Edit Role" : "Add Role" },
  ];

  const {
    data: tableData,
    handleCreate,
    handleUpdate,
  } = useCommonFetch(() => Promise.resolve({}), {
    dataPath: "role.data",
    totalPath: "role.meta.total",
    errorMessage: "Failed to fetch role data!",
    debounceDelay: 500,
  });

  // Fetch permissions data and role detail (if editing)
  useEffect(() => {
    const fetchData = async () => {
      // Start loading role data immediately if editing
      if (id) {
        setLoadingRoleData(true);
      }
      setLoadingPermissions(true);
      try {
        // Fetch permissions structure
        const permissionsRes = await getRolePermissions();
        let permissions = [];
        if (permissionsRes?.permissions) {
          permissions = permissionsRes.permissions;
        } else if (permissionsRes?.data?.permissions) {
          permissions = permissionsRes.data.permissions;
        } else if (Array.isArray(permissionsRes)) {
          permissions = permissionsRes;
        } else if (Array.isArray(permissionsRes?.data)) {
          permissions = permissionsRes.data;
        }

        if (permissions.length > 0) {
          // If editing, fetch role detail and map permissions
          if (id) {
            try {
              const roleRes = await getRoleDetail(id);

              // getRoleDetail now returns the role object directly (from response.data.role)
              // But handle different possible response structures for backward compatibility
              let role = null;
              if (roleRes) {
                // The service should return the role directly, but handle edge cases
                if (roleRes.role) {
                  role = roleRes.role;
                } else if (roleRes.data) {
                  role = roleRes.data;
                } else if (roleRes.name || roleRes.uuid) {
                  // It's already the role object
                  role = roleRes;
                }
              }

              if (role && typeof role === 'object') {
                const InitialData = {
                  name: role?.name || "",
                  abrv: role?.abbreviation || role?.abrv || "",
                  description: role?.description || "",
                };
                setIsEditValue(InitialData);

                // Map role permissions to permissionsData structure
                if (role?.permissions && Array.isArray(role.permissions) && role.permissions.length > 0) {
                  const rolePermissionsArray = role.permissions;

                  const mappedPermissions = permissions.map((module) => {
                    const updatedSubmodules = module.submodules?.map((submodule) => {
                      const updatedPermissions = submodule.permissions?.map((permission) => {
                        // Check if this permission name exists in role.permissions array
                        const isSelected = rolePermissionsArray.includes(permission.name);
                        if (isSelected) {
                        }
                        return {
                          ...permission,
                          status: isSelected,
                        };
                      });

                      // Update submodule status based on all permissions
                      const allPermissionsChecked = updatedPermissions?.every((perm) => perm.status);

                      return {
                        ...submodule,
                        status: allPermissionsChecked,
                        permissions: updatedPermissions,
                      };
                    });

                    // Update module status based on all submodules
                    const allSubmodulesChecked = updatedSubmodules?.every((sub) => sub.status);

                    return {
                      ...module,
                      status: allSubmodulesChecked,
                      submodules: updatedSubmodules,
                    };
                  });

                  setPermissionsData(mappedPermissions);
                } else {
                  setPermissionsData(permissions);
                }
              } else {
                console.warn('Role data is not a valid object:', role);
                toast.error("Invalid role data received from server!");
                setPermissionsData(permissions);
              }
            } catch (error) {
              console.error("Failed to fetch role detail", error);
              console.error("Error details:", error?.response?.data || error?.message || error);
              const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch role detail!";
              toast.error(errorMessage);
              setPermissionsData(permissions);
              // Navigate back if we can't load the role
              setTimeout(() => {
                navigate("/configuration/role", { replace: true });
              }, 2000);
            } finally {
              setLoadingRoleData(false);
            }
          } else {
            // Not editing, just set permissions
            setPermissionsData(permissions);
          }
        }
      } catch (error) {
        console.error("Failed to fetch permissions", error);
        toast.error("Failed to fetch permissions!");
      } finally {
        setLoadingPermissions(false);
      }
    };
    fetchData();
  }, [id]);

  // Form fields
  const roleFields = [
    {
      name: "name",
      label: "Role Name",
      type: "text",
      placeholder: "Enter role name",
      required: true,
      minLength: 2,
      maxLength: 40,
      colSpan: 6,
    },
    {
      name: "abrv",
      label: "Abbreviation",
      type: "text",
      placeholder: "Enter abbreviation",
      minLength: 2,
      maxLength: 40,
      colSpan: 6,
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      required: false,
      placeholder: "Write description here...",
      maxLength: 300,
      colSpan: 12,
    },
  ];

  // Handle module checkbox change
  const handleModuleChange = (moduleIndex, checked) => {
    setPermissionsData((prev) => {
      const updated = [...prev];
      updated[moduleIndex] = {
        ...updated[moduleIndex],
        status: checked,
        submodules: updated[moduleIndex].submodules.map((submodule) => ({
          ...submodule,
          status: checked,
          permissions: submodule.permissions.map((permission) => ({
            ...permission,
            status: checked,
          })),
        })),
      };
      return updated;
    });
  };

  // Handle submodule checkbox change
  const handleSubmoduleChange = (moduleIndex, submoduleIndex, checked) => {
    setPermissionsData((prev) => {
      const updated = [...prev];
      updated[moduleIndex].submodules[submoduleIndex] = {
        ...updated[moduleIndex].submodules[submoduleIndex],
        status: checked,
        permissions: updated[moduleIndex].submodules[submoduleIndex].permissions.map(
          (permission) => ({
            ...permission,
            status: checked,
          })
        ),
      };

      // Update module status based on all submodules
      const allSubmodulesChecked = updated[moduleIndex].submodules.every(
        (sub) => sub.status
      );
      const someSubmodulesChecked = updated[moduleIndex].submodules.some(
        (sub) => sub.status
      );
      updated[moduleIndex].status = allSubmodulesChecked;

      return updated;
    });
  };

  // Handle permission checkbox change — with View dependency enforcement
  const handlePermissionChange = (moduleIndex, submoduleIndex, permissionIndex, checked) => {
    setPermissionsData((prev) => {
      const updated = JSON.parse(JSON.stringify(prev)); // deep clone
      const perms = updated[moduleIndex].submodules[submoduleIndex].permissions;

      const clickedPerm = perms[permissionIndex];
      const isViewPerm = clickedPerm.name?.toLowerCase().endsWith("-view");

      // Apply the toggle
      perms[permissionIndex].status = checked;

      if (checked && !isViewPerm) {
        // Turning ON a non-View permission → force View ON too
        const viewPerm = perms.find((p) => p.name?.toLowerCase().endsWith("-view"));
        if (viewPerm) viewPerm.status = true;
      }

      if (!checked && isViewPerm) {
        // Turning OFF View → force all other permissions OFF too
        perms.forEach((p) => { p.status = false; });
      }

      // Sync submodule status (true only if all perms checked)
      updated[moduleIndex].submodules[submoduleIndex].status = perms.every((p) => p.status);

      // Sync module status
      updated[moduleIndex].status = updated[moduleIndex].submodules.every((s) => s.status);

      return updated;
    });
  };

  const handleSubmit = async (
    values,
    { setSubmitting, resetForm, setErrors }
  ) => {
    setIsSubmitting(true);
    try {
      // Extract only permissions with status: true
      const selectedPermissions = [];
      permissionsData.forEach((module) => {
        module.submodules?.forEach((submodule) => {
          submodule.permissions?.forEach((permission) => {
            if (permission.status === true) {
              selectedPermissions.push(permission.name);
            }
          });
        });
      });

      const payload = {
        name: values.name,
        abrv: values.abrv,
        description: values.description || "",
        permissions: selectedPermissions,
      };

      if (id) {
        try {
          await updateRole(payload, id);
          toast.success("Role updated successfully!");
          navigate("/configuration/role", { replace: true });
        } catch (error) {
          console.error("Failed to update role", error);
          toast.error(error?.response?.data?.message || "Failed to update role!");
          if (error?.response?.data?.errors) {
            setErrors(error.response.data.errors);
          }
        }
      } else {
        try {
          await createRole(payload);
          toast.success("Role created successfully!");
          navigate("/configuration/role", { replace: true });
        } catch (error) {
          console.error("Failed to create role", error);
          toast.error(error?.response?.data?.message || "Failed to create role!");
          if (error?.response?.data?.errors) {
            setErrors(error.response.data.errors);
          }
        }
      }
    } catch (err) {
      if (err?.response?.data?.errors) {
        setErrors(err.response.data.errors);
      }
      if (err?.response?.data?.message) {
        setErrors({ name: err.response.data.message });
      }
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  const handleFormSubmit = () => {
    if (formRef.current) {
      const form = formRef.current.querySelector('form');
      if (form) {
        // Use requestSubmit to trigger validation and submit
        if (form.requestSubmit) {
          form.requestSubmit();
        } else {
          // Fallback for older browsers
          const submitButton = form.querySelector('button[type="submit"]');
          if (submitButton) {
            submitButton.click();
          } else {
            // Create a temporary submit button and click it
            const tempButton = document.createElement('button');
            tempButton.type = 'submit';
            tempButton.style.display = 'none';
            form.appendChild(tempButton);
            tempButton.click();
            form.removeChild(tempButton);
          }
        }
      }
    }
  };

  const handleCancel = () => {
    navigate("/configuration/role");
  };

  // Track which modules are expanded
  const [expandedModules, setExpandedModules] = useState({});
  const toggleModule = (idx) =>
    setExpandedModules((prev) => ({ ...prev, [idx]: !prev[idx] }));

  // Count helpers
  const countModulePerms = (module) => {
    let total = 0, selected = 0;
    module.submodules?.forEach((sub) =>
      sub.permissions?.forEach((p) => { total++; if (p.status) selected++; })
    );
    return { total, selected };
  };

  const totalSelected = permissionsData.reduce((acc, mod) => {
    const { selected } = countModulePerms(mod);
    return acc + selected;
  }, 0);
  const totalPerms = permissionsData.reduce((acc, mod) => {
    const { total } = countModulePerms(mod);
    return acc + total;
  }, 0);

  return (
    <div className="w-full flex flex-col gap-3">
      <PageTableHeader
        breadcrumbs={items}
        isSearch={false}
        isFilter={false}
        buttons={false}
      />

      {/* Role Info Card */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" ref={formRef}>
        <h3 className="text-[13px] font-bold text-blue-900 mb-4 uppercase tracking-wide">Role Information</h3>
        {id && loadingRoleData ? (
          <RoleFormSkeleton />
        ) : (
          <CommonForm
            key={`${id || 'new'}-${isEditValue ? JSON.stringify(isEditValue) : 'empty'}`}
            fields={roleFields}
            initialData={isEditValue || {}}
            onSubmit={handleSubmit}
            enableReinitialize={true}
            showSubmitButton={false}
            showCancelButton={false}
            columnsPerRow={1}
            gap="gap-4"
            rowClassName=""
            fieldClassName="bg-white p-3 rounded shadow-sm"
          >
            <button type="submit" style={{ display: 'none' }} aria-hidden="true" />
          </CommonForm>
        )}
      </div>

      {/* Permissions Card */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm">

        {/* Header + summary bar */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-[13px] font-bold text-blue-900 uppercase tracking-wide">Permissions</h3>
            <p className="text-[12px] text-gray-400 mt-0.5">Checkbox = select all for that module. Arrow button <FiChevronRight className="inline w-3 h-3" /> = expand to see sub-permissions.</p>
          </div>
          {!loadingPermissions && totalPerms > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-[12px] text-gray-500">
                <span className="font-bold text-blue-700">{totalSelected}</span>
                <span className="text-gray-400"> / {totalPerms} selected</span>
              </span>
              <div className="h-2 w-24 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-300"
                  style={{ width: `${totalPerms ? (totalSelected / totalPerms) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Module list */}
        <div className="p-4 flex flex-col gap-2">
          {loadingPermissions ? (
            <div className="text-center py-10 text-gray-400 text-sm">Loading permissions…</div>
          ) : permissionsData.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">No permissions available</div>
          ) : (
            permissionsData.map((module, moduleIndex) => {
              const { total, selected } = countModulePerms(module);
              const isExpanded = !!expandedModules[moduleIndex];
              const someSelected = selected > 0 && selected < total;
              const allSelected = selected === total && total > 0;
              const hasAny = selected > 0;

              return (
                <div
                  key={moduleIndex}
                  className={`rounded-xl border transition-all duration-200 overflow-hidden ${
                    hasAny
                      ? "border-blue-200 shadow-[0_0_0_1px_rgba(59,130,246,0.1)]"
                      : "border-gray-200"
                  }`}
                >
                  {/* Module header row — left = select-all, right = expand only */}
                  <div className={`flex items-center gap-3 px-4 py-3 select-none ${
                    hasAny ? "bg-blue-50/60" : "bg-gray-50"
                  } transition-colors duration-150`}>

                    {/* LEFT zone: checkbox + label = select all */}
                    <label
                      htmlFor={`module-${moduleIndex}`}
                      className="flex items-center gap-2.5 flex-1 cursor-pointer group"
                    >
                      <TriCheckbox
                        id={`module-${moduleIndex}`}
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={(e) => handleModuleChange(moduleIndex, e.target.checked)}
                      />
                      <span className={`text-[13.5px] font-semibold capitalize group-hover:text-blue-700 transition-colors ${
                        hasAny ? "text-blue-900" : "text-gray-700"
                      }`}>
                        {formatLabel(module.module)}
                      </span>
                    </label>

                    {/* Count badge */}
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                      hasAny ? "bg-blue-100 text-blue-700" : "bg-gray-200 text-gray-500"
                    }`}>
                      {selected}/{total}
                    </span>

                    {/* RIGHT zone: chevron button = expand/collapse ONLY */}
                    <button
                      type="button"
                      onClick={() => toggleModule(moduleIndex)}
                      className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 transition-all duration-200 ${
                        isExpanded
                          ? "bg-blue-100 text-blue-600 rotate-90"
                          : "text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                      }`}
                      title={isExpanded ? "Collapse" : "Expand"}
                    >
                      <FiChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Submodules (expand/collapse) */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-2 flex flex-col gap-3 border-t border-gray-100 bg-white">
                      {module.submodules?.map((submodule, submoduleIndex) => {
                        const subTotal = submodule.permissions?.length || 0;
                        const subSelected = submodule.permissions?.filter((p) => p.status).length || 0;
                        const subSome = subSelected > 0 && subSelected < subTotal;
                        const subAll = subSelected === subTotal && subTotal > 0;

                        return (
                          <div key={submoduleIndex} className="flex flex-col gap-2">
                            {/* Submodule header */}
                            <div className="flex items-center gap-2 pt-1">
                              <TriCheckbox
                                id={`submodule-${moduleIndex}-${submoduleIndex}`}
                                checked={subAll}
                                indeterminate={subSome}
                                onChange={(e) =>
                                  handleSubmoduleChange(moduleIndex, submoduleIndex, e.target.checked)
                                }
                              />
                              <label
                                htmlFor={`submodule-${moduleIndex}-${submoduleIndex}`}
                                className="text-[12.5px] font-semibold text-gray-600 capitalize cursor-pointer"
                              >
                                {formatLabel(submodule.name)}
                              </label>
                              {subSelected > 0 && (
                                <span className="text-[10px] text-blue-600 font-medium bg-blue-50 px-1.5 py-0.5 rounded-full">
                                  {subSelected}/{subTotal}
                                </span>
                              )}
                            </div>

                            {/* Permission pills */}
                            <div className="flex flex-wrap gap-2 ml-6">
                              {submodule.permissions?.map((permission, permissionIndex) => {
                                const isOn = !!permission.status;
                                const pillStyle = getActionStyle(permission.name, isOn);
                                const actionLabel = formatLabel(
                                  permission.name?.split("-").slice(-1)[0]
                                );

                                return (
                                  <button
                                    key={permissionIndex}
                                    type="button"
                                    onClick={() =>
                                      handlePermissionChange(
                                        moduleIndex,
                                        submoduleIndex,
                                        permissionIndex,
                                        !isOn
                                      )
                                    }
                                    className={`px-3 py-1 rounded-full text-[11.5px] font-semibold border transition-all duration-150 ${pillStyle}`}
                                  >
                                    {actionLabel}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex justify-end gap-3 pb-4">
        <DefaultButton
          variant="outline"
          name="Cancel"
          onClick={handleCancel}
          disabled={isSubmitting}
        />
        <DefaultButton
          variant="default"
          name={id ? "Update Role" : "Create Role"}
          onClick={handleFormSubmit}
          loading={isSubmitting}
          disabled={isSubmitting}
        />
      </div>
    </div>
  );
};

export default AddRole;
